import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Spin, message, Table, Checkbox, Button } from 'antd';
import axios from 'axios';
import ENVIRONMENT from '../../../../environments';
import AdminMainLayout from '../AdminMainLayout';
import { LoadingOutlined, SaveOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

// Define all possible email settings
const EMAIL_SETTINGS = [
    { key: 'approved_products', label: 'Approved Products' },
    { key: 'approved_rooms', label: 'Approved Rooms' },
    { key: 'rejected_products', label: 'Rejected Products' },
    { key: 'rejected_rooms', label: 'Rejected Rooms' }
];

const EmailSettings = () => {
  const [originalData, setOriginalData] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      const payload = {
        action: "get",
        admin_username: localStorage.getItem("username")
      };

      const response = await axios.post(ENVIRONMENT.ADMIN_EMAIL_SETTINGS_CONTROLLER, payload);
      
      if (response.data && Array.isArray(response.data)) {
        const processedData = response.data.map(company => ({
          ...company,
          email_settings: initializeSettings(company.email_settings || {})
        }));
        setCompaniesData(processedData);
        setOriginalData(JSON.parse(JSON.stringify(processedData))); // Deep copy
      } else {
        message.error('Failed to fetch email settings');
      }
    } catch (error) {
      console.error('Error fetching email settings:', error);
      message.error('Failed to fetch email settings');
    } finally {
      setLoading(false);
    }
  };

  // Initialize settings object with all possible settings set to false
  const initializeSettings = (companySettings = {}) => {
    const settings = {};
    EMAIL_SETTINGS.forEach(setting => {
      settings[setting.key] = companySettings[setting.key] || false;
    });
    return settings;
  };

  // Check if there are any changes
  const hasChanges = () => {
    return JSON.stringify(companiesData) !== JSON.stringify(originalData);
  };

  // Handle checkbox change
  const handleSettingChange = (companyId, settingKey, checked) => {
    setCompaniesData(prevData => 
      prevData.map(company => {
        if (company.company_id === companyId) {
          return {
            ...company,
            email_settings: {
              ...company.email_settings,
              [settingKey]: checked
            }
          };
        }
        return company;
      })
    );
  };

  // Save changes
  const handleSaveChanges = async () => {
    if (!hasChanges()) {
      message.warning('No changes to save');
      return;
    }

    try {
      setSaving(true);
      
      // Prepare the payload in the same format as the API response
      const payload = {
        action: "update",
        admin_username: localStorage.getItem("username"),
        companies: companiesData.map(company => ({
          company_id: company.company_id,
          company_name: company.company_name,
          email_settings: company.email_settings
        }))
      };

      const response = await axios.post(ENVIRONMENT.ADMIN_EMAIL_SETTINGS_CONTROLLER, payload);
      
      message.success('Email settings updated successfully');
      
      // Update original data to reflect the saved state
      setOriginalData(JSON.parse(JSON.stringify(companiesData)));
      
    } catch (error) {
      console.error('Error saving email settings:', error);
      message.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  // Prepare table columns
  const columns = [
    {
      title: 'Company Name',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 200
    },
    ...EMAIL_SETTINGS.map(setting => ({
      title: setting.label,
      key: setting.key,
      dataIndex: ['email_settings', setting.key],
      width: 150,
      render: (checked, record) => (
        <Checkbox 
          className="blue-checkbox"
          checked={checked} 
          onChange={(e) => handleSettingChange(record.company_id, setting.key, e.target.checked)}
        />
      ),
    })),
  ];

  if (loading) {
    return (
      <AdminMainLayout selected="system_management">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <LoadingOutlined spin style={{ fontSize: 48 }} />
          <div style={{ marginTop: '20px' }}>Loading email settings...</div>
        </div>
      </AdminMainLayout>
    );
  }

  return (
    <AdminMainLayout selected="system_management">
      <div style={{ padding: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Title level={2}>Email Settings</Title>
                <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginTop: '8px' }}>
                  Manage email notification settings. You will get daily email report for the selected emails
                </Text>
              </div>
              <Button
                type="primary"
                style={{borderRadius: '4px'}}
                icon={<SaveOutlined />}
                onClick={handleSaveChanges}
                disabled={!hasChanges() || saving}
                loading={saving}
                size="large"
              >
                Save Changes
              </Button>
            </div>
          </Col>

          <Col span={24}>
            <Table
              columns={columns}
              dataSource={companiesData}
              rowKey="company_id"
              scroll={{ x: 'max-content' }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
              }}
              locale={{
                emptyText: 'No company settings found'
              }}
            />
          </Col>
        </Row>
      </div>
    </AdminMainLayout>
  );
};

export default EmailSettings;
